import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { Modal } from "@material-ui/core";
import { ProfilePic } from "../../../pages/DrawerModal/styles";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import labels from "../../../../helpers/labels.json";
import SystemService from "../../../../services/SystemService";
import {
  CheckboxContainer,
  CheckBoxLabel,
  CustomCheckbox,
} from "../../FormInputs";
import CloseIcon from "@material-ui/icons/Close";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";

const EASE_OUT_EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const motionSafe = (animationCss) => css`
  ${animationCss}
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const scaleFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const staggerIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const getInitials = (name) => {
  if (!name) return "NA";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const buildTribunalFields = (userDetails) =>
  [
    {
      label: labels["user.name"],
      value: userDetails?.name,
      section: "contact",
    },
    {
      label: labels["user.email"],
      value: userDetails?.email,
      section: "contact",
    },
    {
      label: labels["user.mobile"],
      value: userDetails?.mobile,
      section: "contact",
    },
    userDetails?.alternateMobileNumber?.length
      ? {
          label: labels["mediator.alternateMobileNumber"],
          value: userDetails?.alternateMobileNumber,
          section: "contact",
        }
      : null,
    {
      label: labels["mediator.qualification"],
      value: userDetails?.qualification || "-",
      section: "professional",
    },
    {
      label: labels["mediator.yearsOfExperience"],
      value: userDetails?.experience || "-",
      section: "professional",
    },
    {
      label: labels["mediator.language"],
      value: userDetails?.languagesKnown || "-",
      section: "professional",
    },
    {
      label: labels["mediator.affiliatedOrg"],
      value: userDetails?.affiliatedOrg || "-",
      section: "professional",
    },
    {
      label: labels["mediator.location"],
      value: userDetails?.location || "-",
      section: "professional",
    },
    {
      label: labels["mediator.specialities"],
      value: userDetails?.specialities || "-",
      section: "professional",
    },
    {
      label: labels["mediator.tagLine"],
      value: userDetails?.tagLine || "-",
      section: "professional",
    },
    userDetails?.institutionOfMediationTraining?.length
      ? {
          label: labels["mediator.institutionOfMediationTraining"],
          value: userDetails?.institutionOfMediationTraining,
          section: "professional",
        }
      : null,
    userDetails?.numberOfMediationsConducted?.length
      ? {
          label: labels["mediator.numberOfMediationsConducted"],
          value: userDetails?.numberOfMediationsConducted,
          section: "professional",
        }
      : null,
  ].filter(Boolean);

export default function DrawerModal({
  caseDetails,
  modal,
  setModal,
  id,
  type = "Case manager",
  details = {},
  removeMediator,
  makePresidingArbitrator,
}) {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [userDetails, setUserDetails] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);

  useEffect(() => {
    async function getUserUserInfo() {
      try {
        setLoader({ state: true, message: "Getting user information" });
        const res = await SystemService.getProfile(id);
        if (res) {
          setUserDetails(res);
          setOpenModal(true);
        }
      } catch (err) {
        const message = getErrorMessage(err);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
    if (id && modal && (type === "Mediator" || type === "Arbitrator")) {
      getUserUserInfo();
    } else {
      setUserDetails(details);
      setOpenModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modal]);

  const handleClose = () => {
    setOpenModal(false);
    setModal(false);
  };

  const handleRemoveMediator = () => {
    if (removeMediator && userDetails?.id) {
      removeMediator(userDetails, caseDetails);
      handleClose();
    }
  };

  const handlePresidingToggle = async () => {
    await makePresidingArbitrator({
      ...userDetails,
      presidingArbitrator: details?.presidingArbitrator,
    });

    handleClose();

    setUserDetails((prev) => ({
      ...prev,
      presidingArbitrator:
        details?.presidingArbitrator === "yes" ? "no" : "yes",
    }));
  };

  const isTribunal = type === "Mediator" || type === "Arbitrator";
  const isPresiding = details?.presidingArbitrator === "yes";
  const accent = isTribunal ? "#00818A" : "#7C5CBF";
  const accentSoft = isTribunal ? "#E0F2F1" : "#F3EEFB";
  const showPhoto = !!userDetails?.avatarUrl && !avatarBroken;

  const tribunalFields = isTribunal ? buildTribunalFields(userDetails) : [];
  const contactFields = tribunalFields.filter((f) => f.section === "contact");
  const professionalFields = tribunalFields.filter(
    (f) => f.section === "professional",
  );

  const subtitle = `${
    userDetails?.role
      ? userDetails.role.charAt(0).toUpperCase() + userDetails.role.slice(1)
      : type
  } Details`;

  return (
    <Modal open={openModal} onClose={handleClose} disableRestoreFocus>
      <ModalOverlay onMouseDown={handleClose}>
        <ModalWrapper onMouseDown={(e) => e.stopPropagation()}>
          <ModalTopBar>
            <TopBarText>
              <TopBarTitle>{type}</TopBarTitle>
              <TopBarSubtitle>{subtitle}</TopBarSubtitle>
            </TopBarText>
            <CloseButton onClick={handleClose} aria-label="Close">
              <CloseIcon style={{ fontSize: 18 }} />
            </CloseButton>
          </ModalTopBar>

          <ModalBody>
            <ProfileHeader>
              <AvatarRing $accent={accent}>
                {showPhoto ? (
                  <ProfilePic
                    src={userDetails.avatarUrl}
                    onError={() => setAvatarBroken(true)}
                  />
                ) : (
                  <InitialsAvatar $accent={accent}>
                    {getInitials(userDetails?.name)}
                  </InitialsAvatar>
                )}
              </AvatarRing>

              <ProfileHeaderInfo>
                <NameRow>
                  <ProfileName>{userDetails?.name || "—"}</ProfileName>
                  {isTribunal && (
                    <RoleBadge $bg={accentSoft} $color={accent}>
                      {type}
                    </RoleBadge>
                  )}
                  {isPresiding && <PresidingBadge>Presiding</PresidingBadge>}
                </NameRow>

                <ContactRow>
                  {userDetails?.email && (
                    <ContactChip>
                      <MailIcon /> {userDetails.email}
                    </ContactChip>
                  )}
                  {userDetails?.mobile && (
                    <ContactChip>
                      <PhoneIcon /> {userDetails.mobile}
                    </ContactChip>
                  )}
                </ContactRow>
              </ProfileHeaderInfo>
            </ProfileHeader>

            {isTribunal ? (
              <>
                {contactFields.length > 0 && (
                  <>
                    <SectionLabel $index={0}>Contact</SectionLabel>
                    <FieldGrid>
                      {contactFields.map((f, i) => (
                        <FieldCard key={f.label} $index={i}>
                          <FieldLabel>{f.label}</FieldLabel>
                          <FieldValue>{f.value}</FieldValue>
                        </FieldCard>
                      ))}
                    </FieldGrid>
                  </>
                )}

                <SectionLabel $index={1}>Professional Details</SectionLabel>
                <FieldGrid>
                  {professionalFields.map((f, i) => (
                    <FieldCard key={f.label} $index={i}>
                      <FieldLabel>{f.label}</FieldLabel>
                      <FieldValue>{f.value}</FieldValue>
                    </FieldCard>
                  ))}
                </FieldGrid>

                <BioCard $index={2}>
                  <BioQuoteMark>&ldquo;</BioQuoteMark>
                  <BioLabel>{labels["mediator.shortbio"]}</BioLabel>
                  <BioText>
                    {userDetails?.shortBio
                      ? userDetails?.shortBio
                      : "No bio added yet."}
                  </BioText>
                </BioCard>

                <ActionsContainer>
                  <ToggleRow>
                    <CheckboxContainer>
                      <CustomCheckbox
                        checked={isPresiding}
                        onChange={handlePresidingToggle}
                      />
                      <CheckBoxLabel for="secretDoc">
                        {isPresiding
                          ? `Remove Presiding ${type === "Mediator" ? "Mediator" : "Arbitrator"}`
                          : `Mark as Presiding ${type === "Mediator" ? "Mediator" : "Arbitrator"}`}
                      </CheckBoxLabel>
                    </CheckboxContainer>
                  </ToggleRow>

                  <RemoveButton onClick={handleRemoveMediator}>
                    <CloseIcon className="icon" />
                    <span>
                      Remove {type === "Mediator" ? "Mediator" : "Arbitrator"}
                    </span>
                  </RemoveButton>
                </ActionsContainer>
              </>
            ) : (
              <>
                <SectionLabel $index={0}>Contact</SectionLabel>
                <FieldGrid>
                  <FieldCard $index={0}>
                    <FieldLabel>{labels["user.name"]}</FieldLabel>
                    <FieldValue>{userDetails?.name || "-"}</FieldValue>
                  </FieldCard>
                  <FieldCard $index={1}>
                    <FieldLabel>{labels["user.email"]}</FieldLabel>
                    <FieldValue>{userDetails?.email || "-"}</FieldValue>
                  </FieldCard>
                  <FieldCard $index={2}>
                    <FieldLabel>{labels["user.mobile"]}</FieldLabel>
                    <FieldValue>{userDetails?.mobile || "-"}</FieldValue>
                  </FieldCard>
                </FieldGrid>
              </>
            )}
          </ModalBody>
        </ModalWrapper>
      </ModalOverlay>
    </Modal>
  );
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 35, 56, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1300;
`;

const ModalWrapper = styled.div`
  background: white;
  border-radius: 22px;
  box-shadow: 0 24px 60px -18px rgba(15, 23, 42, 0.45);
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(148, 163, 184, 0.35);
  ${motionSafe(css`
    animation: ${scaleFadeIn} 0.32s ${EASE_OUT_EXPO} both;
  `)}
`;

const ModalTopBar = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  flex-shrink: 0;
`;

const TopBarText = styled.div`
  min-width: 0;
`;

const TopBarTitle = styled.h2`
  font-size: 18px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  margin: 0;
`;

const TopBarSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 3px 0 0;
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ${EASE_OUT_EXPO},
    transform 0.2s ${EASE_SPRING};

  &:hover {
    background: #e2e8f0;
    transform: rotate(90deg);
  }
`;

export const ModalBody = styled.div`
  padding: 26px 28px 24px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 129, 138, 0.05),
      transparent 35%
    ),
    #ffffff;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding-bottom: 22px;
  margin-bottom: 22px;
  border-bottom: 1px solid #eef1f6;
`;

const AvatarRing = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  padding: 3px;
  flex-shrink: 0;
  background: linear-gradient(
    145deg,
    ${(props) => props.$accent},
    rgba(255, 255, 255, 0.4)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ${EASE_SPRING};

  &:hover {
    transform: scale(1.04);
  }

  ${ProfilePic} {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid white;
  }
`;

const InitialsAvatar = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid white;
  background: ${(props) => props.$accent};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 700;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ProfileHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ProfileName = styled.div`
  font-size: 19px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
`;

const RoleBadge = styled.span`
  padding: 3px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
`;

const PresidingBadge = styled.span`
  padding: 3px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: #fef3e2;
  color: #a55a00;
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
`;

const ContactChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: ${COLORS.INPUT_LABEL};
  text-decoration: none;
  padding: 5px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef1f6;
  transition:
    background 0.2s ${EASE_OUT_EXPO},
    color 0.2s ${EASE_OUT_EXPO};

  &:hover {
    background: #e6f2f3;
    color: #006970;
  }
`;

const MailIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 6-10 7L2 6" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
  </svg>
);

const SectionLabel = styled.div`
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 10px;
  opacity: 0;
  ${motionSafe(css`
    animation: ${staggerIn} 0.4s ${EASE_OUT_EXPO} forwards;
    animation-delay: ${(props) => (props.$index || 0) * 0.06}s;
  `)}

  &:not(:first-child) {
    margin-top: 22px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FieldCard = styled.div`
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 11px 14px;
  background: #fbfcfe;
  opacity: 0;
  ${motionSafe(css`
    animation: ${staggerIn} 0.4s ${EASE_OUT_EXPO} forwards;
    animation-delay: ${(props) => 0.05 + (props.$index || 0) * 0.035}s;
  `)}
  transition:
    border-color 0.2s ${EASE_OUT_EXPO},
    box-shadow 0.2s ${EASE_OUT_EXPO},
    transform 0.2s ${EASE_OUT_EXPO};

  &:hover {
    border-color: #cfd8e8;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
    transform: translateY(-1px);
  }
`;

const FieldLabel = styled.div`
  font-size: 10.5px;
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const FieldValue = styled.div`
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  word-break: break-word;
`;

const BioCard = styled.div`
  position: relative;
  margin-top: 22px;
  padding: 18px 20px 18px 22px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #eef1f6;
  border-left: 3px solid #00818a;
  opacity: 0;
  ${motionSafe(css`
    animation: ${staggerIn} 0.45s ${EASE_OUT_EXPO} forwards;
    animation-delay: ${(props) => (props.$index || 0) * 0.06 + 0.1}s;
  `)}
`;

const BioQuoteMark = styled.div`
  position: absolute;
  top: 2px;
  right: 16px;
  font-size: 42px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #e2e8f0;
  line-height: 1;
  user-select: none;
`;

const BioLabel = styled.div`
  font-size: 10.5px;
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const BioText = styled.div`
  font-size: 13.5px;
  color: ${COLORS.COLOR_DARK};
  line-height: 1.65;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const ActionsContainer = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eef1f6;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
`;

const RemoveButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 9px 16px;
  border: 1.5px solid ${COLORS.REMOVE_RED};
  border-radius: 10px;

  cursor: pointer;
  transition:
    background 0.2s ${EASE_OUT_EXPO},
    transform 0.2s ${EASE_SPRING};

  color: ${COLORS.REMOVE_RED};
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};

  &:hover {
    background: rgba(220, 38, 38, 0.08);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  & .icon {
    font-size: 16px;
    color: ${COLORS.REMOVE_RED};
  }
`;
